import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'

import { getToBeEmailed, ToBeEmailedPerson } from '../../api'
import { useAuth } from '../../context/auth'
import useQuery from '../../context/useQuery'
import List from '../ui/list'
import Spinner from '../ui/spinner'
import FirstInvoiceTabs, { PAGE_SIZE, listPage, toBeEmailedUrl } from './firstInvoiceTabs'
import { formatCalendarDate, formatHourInTimezone } from './sendInstant'

const ToBeEmailed = ({ showTabs = true }: { showTabs?: boolean }) => {
  const { user: admin } = useAuth()
  const params = useQuery()
  const navigate = useNavigate()
  const page = listPage(params, 'toPage')
  const listUrl = toBeEmailedUrl(page)
  const { data, isLoading } = useSWR(listUrl, getToBeEmailed)

  const setPage = (nextPage: number) => {
    const query = new URLSearchParams(params)
    if (nextPage <= 1) query.delete('toPage')
    else query.set('toPage', String(nextPage))
    const search = query.toString()
    navigate(search ? `/admin?${search}` : '/admin')
  }

  const pagination = data?.pagination
  const total = pagination?.total ?? 0
  const label = pagination ? `To be emailed (${total})` : 'To be emailed'
  const people: ToBeEmailedPerson[] = data?.data ?? []
  const currentPage = pagination?.page ?? page
  const totalPages = pagination?.totalPages ?? 1
  const failed = !isLoading && !data

  const headers = [
    { label: 'Email' },
    { label: 'Name' },
    { label: 'Send instant' },
    { label: 'That instant in their timezone' },
    { label: 'Banned' },
    { label: 'Sending' },
  ]
  const rows = people.map((person) => {
    const spellLocale = person.locale || admin?.locale || undefined
    return [
      <Link
        key={person._id}
        to={`/admin/users/${person._id}/invoices`}
        state={{ userEmail: person.email }}
      >
        {person.email}
      </Link>,
      person.name,
      formatCalendarDate(person.sendInstant, admin?.locale),
      formatHourInTimezone(person.sendInstant, person.timezone, spellLocale),
      person.banned ? 'Banned' : '',
      person.sending ? 'Sending' : '',
    ]
  })

  return (
    <section className={showTabs ? 'mt-4' : undefined}>
      {showTabs && (
        <FirstInvoiceTabs
          selected="to-be-emailed"
          toBeEmailedLabel={label}
          alreadyReceivedLabel="Already received"
        />
      )}
      <div className="mt-3">
        <h2 className="h5">{label}</h2>
        {isLoading && (
          <div role="status">
            <Spinner />
            <span className="visually-hidden">Loading</span>
          </div>
        )}
        {failed && <p>Could not load To be emailed.</p>}
        {pagination && total === 0 && <p>No one to be emailed.</p>}
        {pagination && total > 0 && <List headers={headers} rows={rows} />}
        {pagination && total > PAGE_SIZE && (
          <div className="d-flex align-items-center gap-2 mt-2">
            <button
              type="button"
              className="btn btn-light"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              type="button"
              className="btn btn-light"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export default ToBeEmailed
